"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import { dmsApi } from "@/lib/api";
import { useAuth } from "@/hooks/use-auth";
import { ShieldCheck, Loader2, Plus } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface RoleRow {
  _id: string;
  name: string;
  permissions?: string[];
}

interface PermissionOption {
  _id: string;
  key: string;
  description?: string;
}

function permissionGroup(key: string): string {
  if (key.includes("license")) return "Licenses";
  if (key.includes("correspondence")) return "Correspondence";
  if (key.startsWith("share_")) return "Sharing";
  if (key.includes("user") || key.includes("role") || key.includes("department")) return "Admin";
  if (key.includes("folder")) return "Folders";
  if (key.includes("file")) return "Files";
  return "Other";
}

const GROUP_ORDER = ["Files", "Folders", "Sharing", "Licenses", "Correspondence", "Admin", "Other"];

export function RolesPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canCreate = isSuperAdmin || hasPermission("create_role");

  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [permissionsCatalog, setPermissionsCatalog] = useState<PermissionOption[]>([]);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [roleName, setRoleName] = useState("");
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [permSearch, setPermSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadRoles = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await dmsApi.roles.list();
      setRoles(Array.isArray(data) ? data : []);
    } catch (err: unknown) {
      const e = err as { error?: string; message?: string };
      toast({
        variant: "destructive",
        title: "Error",
        description: e?.error ?? e?.message ?? "Failed to load roles.",
      });
      setRoles([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const openCreate = async () => {
    setRoleName("");
    setSelectedKeys(new Set());
    setPermSearch("");
    setCreateOpen(true);
    setLoadingPerms(true);
    try {
      const data = await dmsApi.roles.permissions();
      setPermissionsCatalog(Array.isArray(data) ? data : []);
    } catch (err: unknown) {
      const e = err as { error?: string; message?: string };
      toast({
        variant: "destructive",
        title: "Error",
        description: e?.error ?? e?.message ?? "Failed to load permissions.",
      });
      setPermissionsCatalog([]);
    } finally {
      setLoadingPerms(false);
    }
  };

  const filteredPermissions = useMemo(() => {
    const q = permSearch.trim().toLowerCase();
    if (!q) return permissionsCatalog;
    return permissionsCatalog.filter(
      (p) =>
        p.key.toLowerCase().includes(q) ||
        (p.description && p.description.toLowerCase().includes(q))
    );
  }, [permissionsCatalog, permSearch]);

  const groupedPermissions = useMemo(() => {
    const map = new Map<string, PermissionOption[]>();
    for (const p of filteredPermissions) {
      const g = permissionGroup(p.key);
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(p);
    }
    return GROUP_ORDER.filter((g) => map.has(g)).map((g) => ({
      group: g,
      items: map.get(g)!,
    }));
  }, [filteredPermissions]);

  const toggleKey = (key: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const toggleGroup = (items: PermissionOption[], checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      for (const p of items) {
        if (checked) next.add(p.key);
        else next.delete(p.key);
      }
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      filteredPermissions.forEach((p) => next.add(p.key));
      return next;
    });
  };

  const clearSelection = () => setSelectedKeys(new Set());

  const handleCreate = async () => {
    const name = roleName.trim();
    if (!name) {
      toast({
        variant: "destructive",
        title: "Validation",
        description: "Role name is required.",
      });
      return;
    }
    if (selectedKeys.size === 0) {
      toast({
        variant: "destructive",
        title: "Validation",
        description: "Select at least one permission.",
      });
      return;
    }

    setSubmitting(true);
    try {
      await dmsApi.roles.create({
        name,
        permissions: Array.from(selectedKeys),
      });
      toast({
        title: "Role created",
        description: `"${name}" was created with ${selectedKeys.size} permission(s).`,
      });
      setCreateOpen(false);
      loadRoles();
    } catch (err: unknown) {
      const e = err as { error?: string; message?: string };
      toast({
        variant: "destructive",
        title: "Could not create role",
        description: e?.error ?? e?.message ?? "Request failed.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-auto">
      <Card className="m-6 flex-1 min-h-0 flex flex-col">
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck size={24} />
              Roles
            </CardTitle>
            <CardDescription>
              All roles and their assigned permissions. Create a role and choose which
              permissions it gets.
            </CardDescription>
          </div>
          {canCreate && (
            <Button onClick={openCreate} className="shrink-0 gap-1.5">
              <Plus size={16} />
              Create role
            </Button>
          )}
        </CardHeader>
        <CardContent className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : roles.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No roles found.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Role name</TableHead>
                  <TableHead>Permissions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role._id}>
                    <TableCell className="font-medium align-top">{role.name ?? "—"}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1.5">
                        {Array.isArray(role.permissions) && role.permissions.length > 0 ? (
                          role.permissions.map((p) => (
                            <Badge key={p} variant="secondary" className="text-xs font-normal">
                              {p}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Create role</DialogTitle>
            <DialogDescription>
              Name the role and select the permissions users with this role will have.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto flex-1 min-h-0 py-1">
            <div className="grid gap-2">
              <Label htmlFor="role-name">Role name</Label>
              <Input
                id="role-name"
                placeholder="e.g. Department Viewer"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                disabled={submitting}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Permissions</Label>
                <div className="flex gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={selectAllVisible}
                    disabled={loadingPerms}
                  >
                    Select all
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={clearSelection}
                    disabled={loadingPerms}
                  >
                    Clear
                  </Button>
                </div>
              </div>
              <Input
                placeholder="Search permissions…"
                value={permSearch}
                onChange={(e) => setPermSearch(e.target.value)}
                disabled={loadingPerms}
              />

              {loadingPerms ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
                </div>
              ) : groupedPermissions.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">No permissions found.</p>
              ) : (
                <div className="max-h-[28rem] overflow-y-auto space-y-4 pr-1">
                  {groupedPermissions.map(({ group, items }) => {
                    const allChecked = items.every((p) => selectedKeys.has(p.key));
                    const someChecked = items.some((p) => selectedKeys.has(p.key));
                    return (
                      <div key={group} className="space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={allChecked ? true : someChecked ? "indeterminate" : false}
                            onCheckedChange={(v) => toggleGroup(items, v === true)}
                          />
                          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {group}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            ({items.filter((p) => selectedKeys.has(p.key)).length}/{items.length})
                          </span>
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {items.map((p) => {
                            const checked = selectedKeys.has(p.key);
                            return (
                              <label
                                key={p.key}
                                className={`flex items-start gap-2.5 rounded-md border p-3 cursor-pointer transition-colors ${
                                  checked
                                    ? "border-primary bg-primary/5"
                                    : "hover:bg-muted/40"
                                }`}
                              >
                                <Checkbox
                                  className="mt-0.5"
                                  checked={checked}
                                  onCheckedChange={(v) => toggleKey(p.key, v === true)}
                                />
                                <span className="min-w-0 text-sm leading-snug">
                                  <span className="font-medium block break-all">{p.key}</span>
                                  {p.description ? (
                                    <span className="text-xs text-muted-foreground block mt-0.5 line-clamp-2">
                                      {p.description}
                                    </span>
                                  ) : null}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {selectedKeys.size} permission(s) selected
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={submitting || loadingPerms}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
